/**
 * 계약서 에디터 — JSON 정규화 파이프라인
 *
 *  initialJson / initialText → useEditor.content 까지 거치는 변환:
 *
 *    plainToJson      : 평문(레거시 본문) → ProseMirror JSON
 *    normalizeMarkers : 텍스트 안에 박힌 [[필드:..]] / [varKey] / {{key}} 마커를 노드로 분리
 *    migrateOrphanFields : doc 최상위에 떠 있는 contractField 들을 y 좌표 기준 contractPage 로 이동
 *    ensurePageWrap   : 최상위 비-contractPage 블록을 인접 contractPage 안으로 흡수
 *
 *  jsonToPlainMarker : 저장 시 평문 형식으로 변환 (레거시 호환·검색 인덱스용)
 */
import { A4 } from "./coords";

/** 변수 키/라벨 매핑은 호출자가 주입 (contractTemplate.ts 의 AUTO_VARIABLES) */
export interface VarDef { key: string; label: string }

/** 레거시 한글 키 → 신규 영문 키 */
export const LEGACY_KEY_MAP: Record<string, string> = {
  보호자명: "guardianName", 보호자: "guardianName", 보호자이름: "guardianName",
  닉네임: "guardianNickname", 이메일: "guardianEmail",
  전화번호: "guardianPhone", 보호자연락처: "guardianPhone",
  주소: "guardianAddress",
  펫이름: "petName", 반려동물명: "petName", 반려동물이름: "petName",
  품종: "petBreed", 견종: "petBreed",
  체중: "petWeight", 성별: "petNeutered",
  나이: "petBirthday", 생년월일: "petBirthday",
  특이사항: "petOther", 알러지: "petAllergies", 알레르기: "petAllergies",
  접종: "petVaccinations",
  사업장명: "companyName", 상호: "companyName", 대표자명: "companyName",
  사업자번호: "businessNumber",
  계약일: "contractDate", 작성일: "contractDate",
  시작일: "startDate", 종료일: "endDate", 위탁료: "fee",
};

/** 마커 패턴: [[필드:type|key|label]] / [varKey] / {{key}} */
const MARKER_RE = /\[\[필드:([^|\]]+)\|([^|\]]+)(?:\|([^\]]+))?\]\]|\[([a-zA-Z][a-zA-Z0-9_]*)\]|\{\{\s*([^}\s]+)\s*\}\}/g;

/** 텍스트 노드를 마커 단위로 분리 — text / contractVariable / contractField inline 노드 배열로 변환 */
const splitTextWithMarkers = (
  text: string,
  marks: any[] | undefined,
  vars: VarDef[]
): any[] => {
  if (!text) return [];
  const labelOf = (k: string) => vars.find((v) => v.key === k)?.label ?? k;
  const out: any[] = [];
  let last = 0;
  MARKER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MARKER_RE.exec(text)) !== null) {
    if (m.index > last) {
      const t = text.substring(last, m.index);
      out.push({ type: "text", text: t, ...(marks?.length ? { marks } : {}) });
    }
    if (m[1]) {
      out.push({
        type: "contractField",
        attrs: { fieldType: m[1], fieldKey: m[2], label: m[3] || m[2], alignment: "left" },
      });
    } else if (m[4]) {
      out.push({
        type: "contractVariable",
        attrs: { varKey: m[4], label: labelOf(m[4]) },
      });
    } else if (m[5]) {
      const raw = m[5].trim();
      const mappedKey = LEGACY_KEY_MAP[raw] || raw;
      out.push({
        type: "contractVariable",
        attrs: { varKey: mappedKey, label: labelOf(mappedKey) || raw },
      });
    }
    last = MARKER_RE.lastIndex;
  }
  if (last < text.length) {
    out.push({
      type: "text",
      text: text.substring(last),
      ...(marks?.length ? { marks } : {}),
    });
  }
  return out;
};

/** 트리 전체를 순회하며 텍스트 노드 안의 마커를 분리 */
export const normalizeMarkers = (node: any, vars: VarDef[]): any => {
  if (!node) return node;
  if (node.type === "text" && typeof node.text === "string") {
    return splitTextWithMarkers(node.text, node.marks, vars);
  }
  if (Array.isArray(node.content)) {
    const newContent: any[] = [];
    for (const child of node.content) {
      const replaced = normalizeMarkers(child, vars);
      if (Array.isArray(replaced)) newContent.push(...replaced);
      else newContent.push(replaced);
    }
    return { ...node, content: newContent };
  }
  return node;
};

/** 빈 doc — A4 contractPage 한 장 + 빈 paragraph */
export const emptyDocWithPage = () => ({
  type: "doc",
  content: [{
    type: "contractPage",
    attrs: { pageNumber: 1, width: A4.WIDTH_PX, height: A4.HEIGHT_PX },
    content: [{ type: "paragraph" }],
  }],
});

/** 평문 → ProseMirror JSON (line 단위 paragraph + 마커 노드 분리) */
export const plainToJson = (text: string, vars: VarDef[]): any => {
  const doc: any = { type: "doc", content: [] };
  if (!text) {
    doc.content.push({ type: "paragraph" });
    return doc;
  }
  const lines = text.split("\n");
  const labelOf = (k: string) => vars.find((v) => v.key === k)?.label ?? k;
  const re = /\[\[필드:([^|\]]+)\|([^|\]]+)(?:\|([^\]]+))?\]\]|\[([a-zA-Z][a-zA-Z0-9_]*)\]|\{\{\s*([^}\s]+)\s*\}\}/g;

  for (const line of lines) {
    if (!line.trim()) {
      doc.content.push({ type: "paragraph" });
      continue;
    }
    const para: any = { type: "paragraph", content: [] };
    re.lastIndex = 0;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) para.content.push({ type: "text", text: line.substring(last, m.index) });
      if (m[1]) {
        para.content.push({
          type: "contractField",
          attrs: { fieldType: m[1], fieldKey: m[2], label: m[3] || m[2], alignment: "left" },
        });
      } else if (m[4]) {
        para.content.push({
          type: "contractVariable",
          attrs: { varKey: m[4], label: labelOf(m[4]) },
        });
      } else if (m[5]) {
        const raw = m[5].trim();
        const mappedKey = LEGACY_KEY_MAP[raw] || raw;
        para.content.push({
          type: "contractVariable",
          attrs: { varKey: mappedKey, label: labelOf(mappedKey) || raw },
        });
      }
      last = re.lastIndex;
    }
    if (last < line.length) para.content.push({ type: "text", text: line.substring(last) });
    if (!para.content.length) delete para.content;
    doc.content.push(para);
  }
  if (!doc.content.length) doc.content.push({ type: "paragraph" });
  return doc;
};

/** doc 최상위에 떠 있는 contractField 들을 y 좌표 기준 contractPage 로 이동.
 *  과거 버전(insertContent 가 atom block 을 doc 최상위로 lift) 으로 저장된 JSON 호환성. */
export const migrateOrphanFields = (doc: any): any => {
  if (!doc || !Array.isArray(doc.content)) return doc;
  const pages: any[] = [];
  const orphanFields: any[] = [];
  const remaining: any[] = [];
  for (const n of doc.content) {
    if (n?.type === "contractPage") {
      pages.push(n);
      remaining.push(n);
    } else if (n?.type === "contractField") {
      orphanFields.push(n);
    } else {
      remaining.push(n);
    }
  }
  if (!orphanFields.length) return doc;
  if (!pages.length) {
    return { ...doc, content: remaining.concat(orphanFields) };
  }
  for (const f of orphanFields) {
    const a = f.attrs || {};
    const y = a.y ?? 0;
    const pageIdx = Math.max(0, Math.min(pages.length - 1, Math.floor(y / A4.HEIGHT_PX)));
    const targetPage = pages[pageIdx];
    const adjustedY = y - pageIdx * A4.HEIGHT_PX;
    targetPage.content = (targetPage.content || []).concat([
      { ...f, attrs: { ...a, y: adjustedY } },
    ]);
  }
  return { ...doc, content: remaining };
};

/** 최상위에 contractPage 외 블록이 섞여 있으면 모두 인접 contractPage 안으로 흡수.
 *  Doc 스키마가 "contractPage+" 라 PM 이 setContent 시 거부할 수 있으므로 사전 정규화. */
export const ensurePageWrap = (doc: any): any => {
  if (!doc || !Array.isArray(doc.content)) return doc;
  const items = doc.content;
  if (items.length && items.every((n: any) => n?.type === "contractPage")) return doc;

  const newPages: any[] = [];
  let bucket: any[] = [];
  for (const n of items) {
    if (n?.type === "contractPage") {
      if (bucket.length) {
        if (newPages.length) {
          const prev = newPages[newPages.length - 1];
          prev.content = (prev.content || []).concat(bucket);
        } else {
          newPages.push({
            type: "contractPage",
            attrs: { pageNumber: 1, width: A4.WIDTH_PX, height: A4.HEIGHT_PX },
            content: bucket.slice(),
          });
        }
        bucket = [];
      }
      newPages.push(n);
    } else {
      bucket.push(n);
    }
  }
  if (bucket.length) {
    if (newPages.length) {
      const prev = newPages[newPages.length - 1];
      prev.content = (prev.content || []).concat(bucket);
    } else {
      newPages.push({
        type: "contractPage",
        attrs: { pageNumber: 1, width: A4.WIDTH_PX, height: A4.HEIGHT_PX },
        content: bucket.slice(),
      });
    }
  }

  if (!newPages.length) {
    return emptyDocWithPage();
  }
  newPages.forEach((p: any, i: number) => {
    p.attrs = { ...(p.attrs || {}), pageNumber: i + 1, width: A4.WIDTH_PX, height: A4.HEIGHT_PX };
    if (!p.content || !p.content.length) p.content = [{ type: "paragraph" }];
  });
  return { ...doc, content: newPages };
};

/** contractDate 변수 뒤에 남는 "년 월 일" 라벨 잔류(점·언더바·요일 포함) */
const DATE_RESIDUAL_AFTER =
  /^[\s._]*년[\s._]*월[\s._]*일[\s._]*(?:\(?\s*요일\s*\)?)?/;

/** 날짜 빈칸 잔류물 정리 — contractDate 변수 주변의 "20"(연도 접두)·"년 월 일" 라벨 제거.
 *  AI 임포트가 "20__년 __월 __일" 빈칸 양식에 날짜변수만 끼워 "202026…년 월 일"로 깨지는 것 방지.
 *  contractDate 변수가 있을 때만 인접 텍스트를 손봄 → 본문의 실제 "20"/"년"/"일" 오탐 없음.
 *  "20" 접두가 없는 양식도 자연히 통과(뒤 라벨만 정리). */
const stripDateResiduals = (node: any): any => {
  if (!node || typeof node !== "object") return node;
  if (Array.isArray(node.content)) {
    const c = node.content;
    for (let i = 0; i < c.length; i++) {
      const cur = c[i];
      if (
        cur?.type === "contractVariable" &&
        cur.attrs?.varKey === "contractDate"
      ) {
        const prev = c[i - 1];
        if (prev?.type === "text" && typeof prev.text === "string") {
          // 앞 텍스트의 꼬리 연도접두 "20"(독립 토큰일 때만) 제거
          prev.text = prev.text.replace(/(^|[\s:(])20\s*$/, "$1");
        }
        const next = c[i + 1];
        if (next?.type === "text" && typeof next.text === "string") {
          next.text = next.text.replace(DATE_RESIDUAL_AFTER, "");
        }
      }
    }
    node.content = c.filter(
      (n: any) => !(n?.type === "text" && n.text === ""),
    );
    node.content.forEach(stripDateResiduals);
  }
  return node;
};

/** 통합 정규화 — initialJson 을 받으면 마커분리·필드마이그레이션·페이지래핑 한 번에 적용 */
export const normalizeDoc = (doc: any, vars: VarDef[]): any =>
  stripDateResiduals(ensurePageWrap(migrateOrphanFields(normalizeMarkers(doc, vars))));

/* ─────────────────────────────────────────────────────────────────
 *  JSON → 평문 (저장·검색·레거시 호환)
 * ───────────────────────────────────────────────────────────────── */
export const jsonToPlainMarker = (json: any): string => {
  if (!json || !json.content) return "";
  const out: string[] = [];
  for (const block of json.content) out.push(renderBlock(block));
  return out.join("\n");
};

const renderBlock = (n: any): string => {
  if (!n) return "";
  if (n.type === "paragraph" || n.type === "heading") {
    return (n.content || []).map(renderInline).join("");
  }
  if (n.type === "contractPage") {
    const inner = (n.content || []).map(renderBlock).join("\n");
    const pageNo = n.attrs?.pageNumber ?? 1;
    return `\n[[PAGE:${pageNo}]]\n${inner}`;
  }
  if (n.type === "contractField") {
    const a = n.attrs || {};
    return `[[필드:${a.fieldType}|${a.fieldKey}|${a.label || a.fieldKey}]]`;
  }
  return (n.content || []).map(renderInline).join("");
};

const renderInline = (n: any): string => {
  if (!n) return "";
  if (n.type === "text") return n.text || "";
  if (n.type === "contractVariable") return `[${n.attrs?.varKey || ""}]`;
  if (n.type === "contractField") {
    const a = n.attrs || {};
    return `[[필드:${a.fieldType}|${a.fieldKey}|${a.label || a.fieldKey}]]`;
  }
  if (n.type === "hardBreak") return "\n";
  return (n.content || []).map(renderInline).join("");
};
